import { api } from 'common';
import { useTranslation } from "react-i18next";
import { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { useAlert } from 'components/alert/AlertContext';
import { ThemedInput } from 'components/input/input/ThemedInput';
import { AutocompleteOption, ThemedAutocomplete } from 'components/input/autocomplete/ThemedAutocomplete';
import { Box, Grid, Fade, Card, Alert, Button, Typography, CardContent, InputAdornment } from '@mui/material';
import { AccountBalanceWallet as WalletIcon, Add as AddIcon, Person as PersonIcon } from '@mui/icons-material';

export default function AddMoney() {
    const { addToWallet } = api;
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { showAlert } = useAlert();
    const [amount, setAmount] = useState<string>('');
    const userdata = useSelector(state => state.usersdata);
    const settings = useSelector(state => state.settingsdata.settings);
    const [usersOptions, setUsersOptions] = useState<AutocompleteOption[]>([]);
    const [selectedUser, setSelectedUser] = useState<AutocompleteOption | null>(null);

    useEffect(() => {
        if (userdata.users) {
            const arr: AutocompleteOption[] = [];
            for (let i = 0; i < userdata.users.length; i++) {
                let user = userdata.users[i];
                arr.push({
                    icon: 'Person',
                    value: user.id!,
                    label: `${user.firstName} ${user.lastName} ${user.mobile ? `(${settings?.AllowCriticalEditsAdmin ? user.mobile : "Hidden"})` : ""}  ${settings?.AllowCriticalEditsAdmin ? user.email : "Hidden"}`,
                });
            }
            setUsersOptions(arr);
        }
    }, [userdata.users, settings?.AllowCriticalEditsAdmin]);

    const handleAddBalance = () => {
        if (selectedUser && selectedUser.value && amount && parseFloat(amount) > 0) {
            dispatch(addToWallet(selectedUser.value, amount));
            showAlert({ message: t('success'), type: 'SUCCESS' });
            setAmount('');
            setSelectedUser(null);
        } else {
            showAlert({ message: t('error'), type: 'ERROR' });
        }
    }

    const isFormValid = selectedUser && amount && parseFloat(amount) > 0;

    const selectedUserDetails = useMemo(() => {
        if (selectedUser && userdata.users) {
            const user = userdata.users.find(u => u.id === selectedUser.value);
            return user;
        }
        return null;
    }, [selectedUser, userdata.users]);

    return (
        <>
            <Grid sx={{ paddingTop: '10dvh' }} container alignItems={"center"} justifyContent="center">
                <Grid item xs={12} md={8} lg={6}>
                    <Fade in={true}>
                        <Card elevation={3}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 5, pt: 5 }}>
                                    <WalletIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
                                    <Typography component="h1" variant="h5" sx={{ fontWeight: 600 }}>
                                        {t('add_to_wallet')}
                                    </Typography>
                                </Box>

                                <Grid container spacing={3} sx={{ pb: 5 }}>
                                    <Grid item xs={12}>
                                        <ThemedAutocomplete
                                            showIcons
                                            returnMode="object"
                                            value={selectedUser}
                                            sx={{ width: '100%' }}
                                            options={usersOptions}
                                            label={t('select_user')}
                                            placeholder={t('select_user')}
                                            onChange={(newValue: any) => setSelectedUser(newValue)}
                                        />
                                        {selectedUserDetails && (
                                            <Alert
                                                severity="info"
                                                sx={{ mt: 2 }}
                                                icon={<PersonIcon />}
                                            >
                                                {selectedUserDetails.firstName} {selectedUserDetails.lastName}
                                            </Alert>
                                        )}
                                    </Grid>

                                    <Grid item xs={12}>
                                        <ThemedInput
                                            fullWidth
                                            type="number"
                                            value={amount}
                                            variant="outlined"
                                            label={t('amount')}
                                            placeholder={t('amount')}
                                            onChangeText={text => { setAmount(text) }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        {settings?.symbol}
                                                    </InputAdornment>
                                                ),
                                                inputProps: {
                                                    min: "0",
                                                    step: "0.01"
                                                }
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Box
                                            sx={{
                                                pt: 2,
                                                display: 'flex',
                                                justifyContent: 'flex-end',
                                            }}
                                        >
                                            <Button
                                                variant="contained"
                                                disabled={!isFormValid}
                                                onClick={handleAddBalance}
                                                size="large"
                                                startIcon={<AddIcon />}
                                                sx={{
                                                    minWidth: 200,
                                                    py: 1.5,
                                                    fontSize: '1rem',
                                                    fontWeight: 600,
                                                    boxShadow: 2,
                                                    '&:hover': {
                                                        boxShadow: 4,
                                                        transform: 'translateY(-1px)',
                                                        transition: 'all 0.2s'
                                                    },
                                                    '&:disabled': {
                                                        opacity: 0.7
                                                    }
                                                }}
                                            >
                                                {t('add_to_wallet')}
                                            </Button>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Fade>
                </Grid>
            </Grid>
        </>
    );
}